/**
 * MoneyUtils - Client-side Precision Money Calculations
 * Shared utility class for cent-based monetary operations in frontend
 * Eliminates floating-point precision issues through integer arithmetic
 */
export class MoneyUtils {
    // ==========================================
    // Core Money Conversion & Formatting
    // ==========================================
    /**
     * Convert Euro amount to Cents
     * 12.34 -> 1234
     */
    static euroToCent(euroAmount) {
        if (!Number.isFinite(euroAmount)) {
            throw new Error(`Invalid euro amount: ${euroAmount}`);
        }
        return Math.round(euroAmount * 100);
    }
    /**
     * Convert Cents to Euro amount
     * 1234 -> 12.34
     */
    static centToEuro(centAmount) {
        if (!Number.isInteger(centAmount)) {
            throw new Error(`Cent amount must be integer: ${centAmount}`);
        }
        return centAmount / 100;
    }
    /**
     * Format currency for display (German locale)
     * 1234 cents -> "12,34 €"
     */
    static formatCurrency(centAmount, locale = 'de-DE') {
        const euroAmount = this.centToEuro(centAmount);
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: 'EUR'
        }).format(euroAmount);
    }
    // ==========================================
    // Basic Arithmetic Operations
    // ==========================================
    /**
     * Add two cent amounts
     */
    static addCent(amount1, amount2) {
        return amount1 + amount2;
    }
    /**
     * Subtract cent amounts
     */
    static subtractCent(amount1, amount2) {
        return amount1 - amount2;
    }
    /**
     * Multiply cent amount by factor
     * Example: 1000 cents * 1.19 = 1190 cents (VAT)
     */
    static multiplyCent(centAmount, factor) {
        return Math.round(centAmount * factor);
    }
    /**
     * Calculate percentage of cent amount
     * Example: 10% of 1000 cents = 100 cents
     */
    static percentageCent(centAmount, percentage) {
        return Math.round(centAmount * (percentage / 100));
    }
    // ==========================================
    // Comparison Operations
    // ==========================================
    /**
     * Safe comparison for cent amounts
     */
    static isEqual(amount1, amount2) {
        return amount1 === amount2;
    }
    static isGreater(amount1, amount2) {
        return amount1 > amount2;
    }
    static isLess(amount1, amount2) {
        return amount1 < amount2;
    }
    // ==========================================
    // Discount Calculations
    // ==========================================
    /**
     * Calculate discount percentage between original and discounted price
     */
    static calculateDiscountPercentage(originalCent, discountedCent) {
        if (originalCent <= 0)
            return 0;
        const savings = originalCent - discountedCent;
        return Math.round((savings / originalCent) * 100);
    }
    /**
     * Apply discount percentage to cent amount
     */
    static applyDiscountPercentage(centAmount, discountPercent) {
        const discountAmount = this.percentageCent(centAmount, discountPercent);
        return centAmount - discountAmount;
    }
    // ==========================================
    // Validation & Array Operations
    // ==========================================
    /**
     * Validate that a number represents valid cent amount (integer)
     */
    static isValidCentAmount(amount) {
        return Number.isInteger(amount) && amount >= 0;
    }
    /**
     * Sum array of cent amounts
     */
    static sumCent(amounts) {
        return amounts.reduce((sum, amount) => sum + amount, 0);
    }
    /**
     * Get minimum of cent amounts
     */
    static minCent(...amounts) {
        return Math.min(...amounts);
    }
    /**
     * Get maximum of cent amounts
     */
    static maxCent(...amounts) {
        return Math.max(...amounts);
    }
    // ==========================================
    // Display Formatting Extensions
    // ==========================================
    /**
     * Formatierung mit Währungssymbol
     * @param cent - Cent-Betrag als Integer
     * @returns Formatierter Euro-String
     */
    static formatWithCurrency(cent) {
        return MoneyUtils.formatCurrency(cent, '€');
    }
    /**
     * Formatiert Minimum Order Betrag für Display
     * @param cent - Mindestbestellwert in Cent
     * @param deliveryType - 'pickup' | 'delivery'
     * @returns Formatierter String mit Kontext
     */
    static formatMinimumOrder(cent, deliveryType) {
        const formatted = MoneyUtils.formatCurrency(cent);
        const context = deliveryType === 'pickup' ? 'Abholung' : 'Lieferung';
        return `Mindestbestellwert (${context}): ${formatted}`;
    }
    /**
     * Formatiert Service Fee mit Beschreibung
     * @param cent - Service Fee in Cent
     * @returns Formatierter String
     */
    static formatServiceFee(cent) {
        if (cent === 0) {
            return 'Servicegebühr: kostenlos';
        }
        return `Servicegebühr: ${MoneyUtils.formatCurrency(cent)}`;
    }
    /**
     * Formatiert Delivery Fee mit Beschreibung
     * @param cent - Delivery Fee in Cent
     * @returns Formatierter String
     */
    static formatDeliveryFee(cent) {
        if (cent === 0) {
            return 'Lieferung: kostenlos';
        }
        return `Liefergebühr: ${MoneyUtils.formatCurrency(cent)}`;
    }
    /**
     * Formatiert Tax Amount mit Prozentsatz
     * @param cent - Steuerbetrag in Cent
     * @param taxRate - Steuersatz in Prozent
     * @returns Formatierter String
     */
    static formatTax(cent, taxRate) {
        const formatted = MoneyUtils.formatCurrency(cent);
        return `inkl. ${taxRate}% MwSt.: ${formatted}`;
    }
    /**
     * Formatiert Preis für Produkt-Karten
     * @param cent - Preis in Cent
     * @param showCurrency - Währungssymbol anzeigen
     * @returns Formatierter Preis für UI
     */
    static formatProductPrice(cent, showCurrency = true) {
        if (showCurrency) {
            return MoneyUtils.formatCurrency(cent);
        }
        // Format ohne Währungssymbol - verwende centToEuro und formatiere manuell
        return MoneyUtils.centToEuro(cent).toFixed(2).replace('.', ',');
    }
    /**
     * Erstellt Preis-Range String für variable Preise
     * @param minCent - Minimaler Preis in Cent
     * @param maxCent - Maximaler Preis in Cent
     * @returns Range-String (z.B. "5,00 € - 12,00 €")
     */
    static formatPriceRange(minCent, maxCent) {
        if (minCent === maxCent) {
            return MoneyUtils.formatCurrency(minCent);
        }
        return `${MoneyUtils.formatCurrency(minCent)} - ${MoneyUtils.formatCurrency(maxCent)}`;
    }
    // ==========================================
    // Service Fee Methods (from ServiceFeeService)
    // ==========================================
    /**
     * Formatiert Service Fee für Display
     * @param calculation - Service Fee Berechnung vom Backend
     * @returns Display-Objekt
     */
    static formatServiceFeeForDisplay(calculation) {
        return {
            amountCent: calculation.amountCent,
            formatted: MoneyUtils.formatCurrency(calculation.amountCent),
            description: MoneyUtils.formatServiceFeeDescription(calculation),
            type: calculation.type,
            isActive: calculation.amountCent > 0,
            amount: calculation.amount,
            ...(process.env.NODE_ENV === 'development' && {
                debug: {
                    subtotalCent: calculation.subtotalCent,
                    percentage: calculation.details.percentage,
                    fixedAmountCent: calculation.details.fixedAmountCent,
                    maxValueCent: calculation.details.maxValueCent,
                    cappedByMax: calculation.details.cappedByMax
                }
            })
        };
    }
    /**
     * Formatiert Service Fee Beschreibung
     * @param calculation - Service Fee Berechnung
     * @returns Beschreibung
     */
    static formatServiceFeeDescription(calculation) {
        if (calculation.type === 'none' || calculation.amountCent === 0) {
            return 'Keine Service-Gebühr';
        }
        const { details } = calculation;
        let description = '';
        if (details.percentage && details.percentage > 0) {
            description += `${details.percentage}% Service-Gebühr`;
            if (details.fixedAmountCent && details.fixedAmountCent > 0) {
                const fixedFormatted = MoneyUtils.formatCurrency(details.fixedAmountCent);
                description += ` + ${fixedFormatted} Grundgebühr`;
            }
        }
        else if (details.fixedAmountCent && details.fixedAmountCent > 0) {
            const fixedFormatted = MoneyUtils.formatCurrency(details.fixedAmountCent);
            description = `${fixedFormatted} Service-Gebühr`;
        }
        if (details.cappedByMax && details.maxValueCent) {
            const maxFormatted = MoneyUtils.formatCurrency(details.maxValueCent);
            description += ` (max. ${maxFormatted})`;
        }
        return description;
    }
    /**
     * Prüft ob Service Fee aktiv ist
     * @param calculation - Service Fee Berechnung
     * @returns true wenn Service Fee > 0
     */
    static isServiceFeeActive(calculation) {
        return calculation.amountCent > 0;
    }
    /**
     * Erstellt leere Service Fee (wenn deaktiviert)
     * @param subtotalCent - Zwischensumme in Cent
     * @returns Leere Service Fee
     */
    static createEmptyServiceFee(subtotalCent) {
        return {
            amountCent: 0,
            subtotalCent,
            amount: 0,
            type: 'none',
            details: {
                subtotalCent,
                subtotal: subtotalCent / 100
            }
        };
    }
}
export default MoneyUtils;
